import React from "react";

import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { makeStyles } from "tss-react/mui";
import { ButtonBase, Grid, Typography } from "@mui/material";
import { ImageNotSupportedOutlined } from "@mui/icons-material";

const useStyles = makeStyles()((theme) => ({
  buttonBase: {
    display: "flex",
    flexDirection: "column",
  },
  innerButton: {
    alignItems: "center",
    borderRadius: "8px",
    flexDirection: "column",
    justifyContent: "space-around",
    padding: theme.spacing(3),
    minHeight: "15vh",
    textDecoration: "underline",
    fontSize: theme.typography.body1.fontSize,

    "& > input": {
      visibility: "hidden",
      height: 0,
    },
  },
}));

const getImgData = (otherProps: FileProps | DataProps): [string, string] => {
  let name = "";
  let src = "";
  if ("currentFile" in otherProps && otherProps.currentFile?.length) {
    name = otherProps.currentFile[0].name;
    src = URL.createObjectURL(otherProps.currentFile[0]);
  } else if ("fileName" in otherProps) {
    name = otherProps.fileName || "";
    src = otherProps.fileSrc || "";
  }
  return [name, src];
};

export const FileUpload = ({
  text,
  onFileUpload,
  fullWidth = false,
  hasError = false,
  ...otherProps
}: FileUploadProps) => {
  const { classes } = useStyles();

  const [fileName, fileSrc] = getImgData(otherProps);
  const hasUploadedFile: boolean = !!(fileName || fileSrc);

  const handleFileUpload = ({ target }) => {
    const file = target.files.length ? target.files : null;
    if (file) {
      onFileUpload(file);
    }
  };

  return (
    <ButtonBase component="label" className={classes.buttonBase}>
      <Grid
        item
        container
        className={classes.innerButton}
        sx={{
          width: fullWidth ? "100%" : "90%",
          border: ({ palette }) =>
            `1px dashed ${
              hasError ? palette.error.main : palette.primary.main
            }`,
          /* if there's a valid imgSrc, display it */
          background: fileSrc ? `center no-repeat url(${fileSrc})` : "",
        }}
      >
        <input type="file" onChange={handleFileUpload} />
        {/* If no file was uploaded, display cloud symbol and ask for upload */}
        {!hasUploadedFile && (
          <CloudUploadOutlinedIcon
            fontSize="large"
            color={hasError ? "error" : "primary"}
          />
        )}
        {!hasUploadedFile && <Typography>Enviar imagens do imóvel</Typography>}

        {/* if a file has been uploaded, but only the filename was provided show placeholder icon + filename */}
        {hasUploadedFile && !fileSrc && (
          <ImageNotSupportedOutlined fontSize="large" />
        )}
        {hasUploadedFile && !fileSrc && (
          <Typography variant="caption">{fileName}</Typography>
        )}
      </Grid>

      {/* Always display alteration CTA when image was provided */}
      {hasUploadedFile && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ textDecoration: "underline", mt: 1 }}
        >
          {text}
        </Typography>
      )}
    </ButtonBase>
  );
};

export type FileUploadProps = BaseUploadProps & (FileProps | DataProps);

type BaseUploadProps = {
  text: string;
  onFileUpload: (file: HTMLInputElement["files"]) => void;
  fullWidth?: boolean;
  hasError?: boolean;
};

/** Mostly for "create" forms, where a new file is being uploaded */
type FileProps = {
  currentFile?: HTMLInputElement["files"];
};

/** Mostly for "edit" forms, where an existing file is being displayed and possibly updated */
type DataProps = {
  fileName: string;
  fileSrc: string;
};
